import {Box, Slider, Stack, Typography, mergeSlotProps} from '@mui/material';
import {SliderThumb} from '@mui/material/Slider';
import {styled} from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import NumberField from '@src/components/common/NumberField.jsx';

const StyledSlider = styled(Slider)(({theme}) => ({
  height: 2,
  margin: theme.spacing(0, 1.5),
  '& .MuiSlider-thumb': {
    backgroundColor: theme.vars.palette.background.paper,
    border: '1px solid currentColor',
    boxShadow: '0 1px 1px 0 rgba(0,0,0,0.32)',
    '&:hover': {
      boxShadow: `0px 0px 0px 8px rgba(${theme.vars.palette.primary.mainChannel} / 0.16)`,
    },
    height: 27,
    width: 27,
    '& .bar': {
      backgroundColor: 'currentColor',
      height: 9,
      marginLeft: 1,
      marginRight: 1,
      width: 1,
    },
  },
  '& .MuiSlider-track': {
    height: 1,
  },
  '& .MuiSlider-rail': {
    color: theme.vars.palette.grey[300],
    height: 2,
    opacity: 1,
  },
}));

const ThumbComponent = ({children, ...props}) => {
  return (
    <SliderThumb {...props}>
      {children}
      <span className="bar" />
      <span className="bar" />
      <span className="bar" />
    </SliderThumb>
  );
};

ThumbComponent.propTypes = {
  children: PropTypes.node,
};

const DEFAULT_VALUES = [0, 1];
const identityFn = x => x;

const SliderFilter = ({
  min = 0,
  max = 1,
  step = 1 / 100,
  values: valuesProp = DEFAULT_VALUES,
  unit,
  rootProps = {},
  scale = identityFn,
  resetScale = identityFn,
  ...props
}) => {
  const [values, setValues] = useState(valuesProp);
  const [numberFields, setNumberFields] = useState(
    valuesProp.map(val => scale(val)),
  );
  const hasSecondValue = values.length !== 1;

  useEffect(() => {
    setValues(valuesProp);
  }, [valuesProp]);

  useEffect(() => {
    setNumberFields(values.map(val => scale(val)));
  }, [values, scale]);

  const numberFieldProps = {
    min: scale(min),
    max: scale(max),
    unit,
    step: scale(step),
  };

  const onSliderChange = (_, newValues) => {
    setValues(newValues);
  };

  const onNumberFieldChange = index => event => {
    const {value} = event.target;
    const newValue = value ? Number(value) : '';

    const clonedValues = values.slice();
    const clonedNumberFields = numberFields.slice();
    clonedNumberFields[index] = newValue;

    if (typeof newValue === 'number') {
      const resetValue = resetScale(newValue);
      //cap values for sliders
      const cappedValue =
        resetValue < min ? min : resetValue > max ? max : resetValue;
      clonedValues[index] = cappedValue;
      setValues(clonedValues);
      props.onChangeComplete(clonedValues);
    }

    // keep what the user entered
    setNumberFields(clonedNumberFields);
  };

  const onNumberFieldBlur = index => event => {
    const {value} = event.target;
    const resetValue = resetScale(value);

    if (resetValue < min || resetValue > max) {
      setNumberFields(nf => (index === 0 ? [min, nf[1]] : [nf[0], max]));
    } else if (value === '') {
      setNumberFields(values.map(val => scale(val)));
    }
  };

  const newRootProps = mergeSlotProps(rootProps, {
    sx: {paddingBlock: 2, width: '100%'},
  });

  return (
    <Box {...newRootProps}>
      <Stack
        direction="row"
        sx={{alignItems: 'baseline', gap: 1, marginBottom: 1, width: '100%'}}
      >
        <Typography component="span">{props.name}</Typography>

        <Typography component="span" variant="caption">
          {scale(min)} – {scale(max)}
          {unit}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 1,
        }}
      >
        <StyledSlider
          components={{Thumb: ThumbComponent}}
          min={min}
          max={max}
          onChange={onSliderChange}
          onChangeCommitted={(_, value) => {
            props.onChangeComplete(value);
          }}
          step={step}
          value={values.slice()}
        />
      </Box>

      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 1,
          width: '100%',
        }}
      >
        <NumberField
          {...numberFieldProps}
          onBlur={onNumberFieldBlur(0)}
          onChange={onNumberFieldChange(0)}
          pattern=""
          value={numberFields[0]}
        />
        {hasSecondValue && (
          <>
            <Typography sx={{marginInline: 1}}>–</Typography>

            <NumberField
              {...numberFieldProps}
              onBlur={onNumberFieldBlur(1)}
              onChange={onNumberFieldChange(1)}
              value={numberFields[1]}
              fullWidth
            />
          </>
        )}
      </Box>
    </Box>
  );
};

SliderFilter.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  values: PropTypes.array,
  unit: PropTypes.string,
  scale: PropTypes.func,
  resetScale: PropTypes.func,
  name: PropTypes.string,
  onChangeComplete: PropTypes.func,
  dataQe: PropTypes.string,
  rootProps: PropTypes.object,
};

export default SliderFilter;
